import { useMemo, useState } from 'react';
import {
  HandRaisedIcon,
  ArrowUturnLeftIcon,
  PlayIcon,
  PlusIcon,
  ChevronDownIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import {
  AppBadge,
  Badge,
  Btn,
  Card,
  StatusDot,
  Sparkline,
  cx,
  makeSpark,
} from '../../../components/admin/console';
import { useApps, useBuilds, usePointers } from './data/hooks';
import type { Build, ChannelPointer, OtaAppId } from './data/types';
import {
  OTA_RELEASE_CHANNEL_ORDER,
  channelCardTitle,
  channelLabel,
  hasGradualRollout,
  isBetaReleaseChannel,
} from './data/channelLabels';
import { signingKeyBadgeTone, signingKeyShortLabel } from './data/signingLabels';
import { formatDistanceToNow } from 'date-fns';
import { canDo, type UserRole } from './data/permissions';
import { useAuth } from '../../../providers/AuthProvider';

export function OtaChannelsPage() {
  const { user } = useAuth();
  const role = (user?.role ?? 'admin') as UserRole;
  const { data: apps = [] } = useApps();
  const { data: pointers = [] } = usePointers();
  const { data: builds = [] } = useBuilds();
  const [selectedApp, setSelectedApp] = useState<OtaAppId>('vn');

  const pointersByChannel = useMemo(() => {
    const map = new Map(
      pointers.filter((p) => p.app === selectedApp).map((p) => [p.channel, p]),
    );
    return map;
  }, [pointers, selectedApp]);

  const app = apps.find((a) => a.id === selectedApp);
  const hasAnyPointer = pointersByChannel.size > 0;

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
            OTA Release Channels
          </div>
          <h1 className="text-2xl font-bold text-white mt-1 flex items-center gap-3">
            <AppBadge app={selectedApp} size="md" />
            {app?.name ?? selectedApp}
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Each card is a release channel (where devices look for updates). Standard and
            platform signing keys ship the same version code together unless noted below.
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-800/60 border border-slate-700">
          {apps.map((a) => (
            <button
              type="button"
              key={a.id}
              onClick={() => setSelectedApp(a.id)}
              className={cx(
                'px-2.5 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 font-mono',
                selectedApp === a.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white',
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.color }} />
              {a.id}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl">
        {OTA_RELEASE_CHANNEL_ORDER.map((channelId) => {
          const pointer = pointersByChannel.get(channelId);
          if (!pointer) return null;
          return (
            <ChannelCard
              key={pointer.channel}
              pointer={pointer}
              role={role}
              builds={builds.filter((b) => b.appDir === selectedApp)}
              currentBuild={builds.find(
                (b) => b.appDir === pointer.app && b.versionCode === pointer.current,
              )}
              previousBuild={builds.find(
                (b) => b.appDir === pointer.app && b.versionCode === pointer.previous,
              )}
            />
          );
        })}
        {!hasAnyPointer && (
          <Card className="col-span-2 text-center text-sm text-slate-500">
            No release channels configured for {app?.name ?? selectedApp}.
          </Card>
        )}
      </div>
    </div>
  );
}

interface ChannelCardProps {
  pointer: ChannelPointer;
  builds: Build[];
  currentBuild?: Build;
  previousBuild?: Build;
  role: UserRole;
}

function ChannelCard({ pointer, builds, currentBuild, previousBuild, role }: ChannelCardProps) {
  const [policyOpen, setPolicyOpen] = useState(false);
  const isBeta = isBetaReleaseChannel(pointer.channel);
  const gradualRollout = hasGradualRollout(pointer.channel);
  const status = pointer.status;
  const ctx = { app: pointer.app, channel: pointer.channel };
  const canPromote = canDo(role, 'promote', ctx);
  const canHalt = canDo(role, 'halt', ctx);
  const canRollback = canDo(role, 'rollback', ctx);
  const canResume = canDo(role, 'resume', ctx);
  const canWiden = canDo(role, 'widen', ctx);

  const atVc = builds.filter((b) => b.versionCode === pointer.current);
  const standardBuild = atVc.find((b) => b.signingKey === 'standard');
  const platformBuild = atVc.find((b) => b.signingKey === 'platform');

  const statusLabel =
    status === 'rolling' ? 'rolling'
    : status === 'halted' ? 'halted'
    : status === 'healthy' ? 'healthy'
    : 'no pointer';
  const statusTone =
    status === 'rolling' ? 'warning'
    : status === 'halted' ? 'error'
    : 'success';

  return (
    <Card padded={false} className="overflow-hidden">
      <div
        className={cx(
          'px-5 py-4 flex items-center justify-between border-b',
          status === 'halted'  ? 'bg-red-500/5 border-red-500/20'
          : status === 'rolling' ? 'bg-amber-500/5 border-amber-500/20'
          : 'bg-slate-900/40 border-slate-700/60',
        )}
      >
        <div className="flex items-center gap-3">
          <StatusDot status={status} />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-white">{channelCardTitle(pointer.channel)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {channelLabel(pointer.channel)} · promoted{' '}
              {formatDistanceToNow(new Date(pointer.promotedAt), { addSuffix: true })} by{' '}
              <span className="font-mono text-slate-400">{pointer.promotedBy}</span>
            </div>
          </div>
        </div>
        <Badge tone={statusTone} dot>
          {statusLabel}
        </Badge>
      </div>

      <div className="px-5 py-3 border-b border-slate-700/50 bg-slate-900/30">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
          APKs at vc {pointer.current}
        </div>
        <div className="flex flex-wrap gap-2">
          <SigningKeyPill
            label={signingKeyShortLabel('standard')}
            present={!!standardBuild}
            tone={signingKeyBadgeTone('standard')}
          />
          <SigningKeyPill
            label={signingKeyShortLabel('platform')}
            present={!!platformBuild}
            tone={signingKeyBadgeTone('platform')}
          />
        </div>
        {!standardBuild && !platformBuild && (
          <p className="text-[11px] text-slate-500 mt-2">No matching artefacts in the build registry.</p>
        )}
      </div>

      <div className="px-5 py-4 flex items-baseline gap-3 border-b border-slate-700/50">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">vc</div>
        <div className="text-3xl font-mono font-bold text-white leading-none">
          {pointer.current}
        </div>
        <div className="text-sm font-mono text-slate-300">{currentBuild?.versionName ?? '—'}</div>
        <div className="flex-1" />
        {previousBuild && (
          <div className="text-[11px] text-slate-500 font-mono">prev {pointer.previous}</div>
        )}
      </div>

      <div className="px-5 py-4 space-y-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider">
          <span className="text-slate-500 font-bold">Policy</span>
          <span className="font-mono text-slate-300">
            {status === 'halted' ? 'halted' : pointer.policy}
          </span>
        </div>

        {pointer.policy === 'exposure' && pointer.maxDevices != null && (
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[11px] text-slate-400">Exposure</span>
              <span className="text-xs font-mono text-white">
                {pointer.served} / max {pointer.maxDevices}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-700/60 overflow-hidden">
              <div
                className={cx(
                  'h-full transition-all',
                  status === 'halted'
                    ? 'bg-red-500'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500',
                )}
                style={{
                  width: `${Math.min(100, (pointer.served / pointer.maxDevices) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <Metric label="devices on build" value={pointer.served} />
          <Metric
            label="healthy hours"
            value={`${pointer.healthyHours}h / ${pointer.minHealthyHours}h`}
            tone={pointer.healthyHours >= pointer.minHealthyHours ? 'good' : 'warn'}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            devices 14d
          </span>
          <span className="text-cyan-400 flex-1">
            <Sparkline
              data={makeSpark(pointer.current * 11 + pointer.channel.length, pointer.served || 3)}
              width={160}
              height={22}
            />
          </span>
        </div>
      </div>

      <div className="border-b border-slate-700/50">
        <button
          type="button"
          onClick={() => setPolicyOpen((o) => !o)}
          className="w-full px-5 py-2.5 flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-400 hover:text-white hover:bg-slate-800/40"
        >
          <span className="font-bold">Rollout policy</span>
          <ChevronDownIcon
            className={cx('w-3.5 h-3.5 transition-transform', policyOpen && 'rotate-180')}
          />
        </button>
        {policyOpen && (
          <div className="px-5 pb-4 pt-1 grid grid-cols-2 gap-3">
            <PolicyField label="policy">
              <select
                className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs font-mono text-white w-full"
                defaultValue={pointer.policy}
              >
                <option value="exposure">exposure</option>
                <option value="open">open</option>
                <option value="halted">halted</option>
              </select>
            </PolicyField>
            <PolicyField label="maxDevices">
              <input
                type="number"
                defaultValue={pointer.maxDevices ?? ''}
                className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs font-mono text-white w-full"
              />
            </PolicyField>
            <PolicyField label="minHealthyHours">
              <input
                type="number"
                defaultValue={pointer.minHealthyHours}
                className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs font-mono text-white w-full"
              />
            </PolicyField>
            <PolicyField label="haltOnAnyRollback">
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  defaultChecked={pointer.haltOnRollback}
                  className="rounded border-slate-600 bg-slate-900 text-cyan-500"
                />
                auto-halt on rollback
              </label>
            </PolicyField>
          </div>
        )}
      </div>

      <div className="px-5 py-3 flex items-center gap-2 flex-wrap bg-slate-900/30">
        {gradualRollout && status !== 'halted' && (
          <Btn
            size="sm"
            variant="outline"
            leftIcon={<PlusIcon className="w-3 h-3" />}
            disabled={!canWiden}
          >
            Widen
          </Btn>
        )}
        <Btn
          size="sm"
          variant="warning"
          leftIcon={<HandRaisedIcon className="w-3 h-3" />}
          disabled={!canHalt || status === 'halted'}
        >
          Halt
        </Btn>
        {status === 'halted' && (
          <Btn size="sm" variant="primary" leftIcon={<PlayIcon className="w-3 h-3" />} disabled={!canResume}>
            Resume
          </Btn>
        )}
        <Btn
          size="sm"
          variant="danger"
          leftIcon={<ArrowUturnLeftIcon className="w-3 h-3" />}
          disabled={!canRollback}
        >
          Rollback to {pointer.previous}
        </Btn>
        <div className="flex-1" />
        {isBeta && (
          <Btn size="sm" variant="ghost" leftIcon={<MapPinIcon className="w-3 h-3" />}>
            Pin to build…
          </Btn>
        )}
        {gradualRollout && status === 'healthy' && canPromote && (
          <span className="text-[10px] text-slate-500">
            Use Builds → Promote to push a newer vc.
          </span>
        )}
      </div>
    </Card>
  );
}

function SigningKeyPill({
  label,
  present,
  tone,
}: {
  label: string;
  present: boolean;
  tone: 'warning' | 'blue';
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-semibold',
        present
          ? tone === 'warning'
            ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
            : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'
          : 'bg-slate-800/60 text-slate-500 border-slate-700 border-dashed',
      )}
    >
      <span
        className={cx(
          'w-1.5 h-1.5 rounded-full',
          present ? (tone === 'warning' ? 'bg-amber-400' : 'bg-cyan-400') : 'bg-slate-600',
        )}
      />
      {label}
      {!present && <span className="text-slate-600 font-normal">—</span>}
    </span>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string | null | undefined;
  tone?: 'good' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'warn' ? 'text-amber-400' : tone === 'bad' ? 'text-red-400' : 'text-emerald-400';
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div
        className={cx(
          'text-sm font-mono font-bold mt-0.5',
          value != null ? toneClass : 'text-slate-500',
        )}
      >
        {value ?? '—'}
      </div>
    </div>
  );
}

function PolicyField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}

export default OtaChannelsPage;
